import { useEffect, useState } from 'react';
import { sortExecutionChatMessages } from '@/components/execution-chat/sortMessages';
import { supabase } from '@/lib/supabase';
import { extractLaunchInputs, type LaunchInput } from '@/lib/execution-launch-inputs';
import type { ExecutionChatMessageRow } from '@shared/types/execution-chat';
import type { ExecutionStep } from '@shared/types/flow-executions';

export interface SnapshotDomino {
  position?: number;
  action_type?: string;
  label?: string;
}

export interface ToolExecutionTraceState {
  loading: boolean;
  error: string | null;
  steps: ExecutionStep[];
  messages: ExecutionChatMessageRow[];
  snapshotDominos: SnapshotDomino[];
  launchInputs: LaunchInput[];
}

const EMPTY_EXECUTION_TRACE_STATE: ToolExecutionTraceState = {
  loading: false,
  error: null,
  steps: [],
  messages: [],
  snapshotDominos: [],
  launchInputs: [],
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const mergeExecutionSteps = (
  previous: ExecutionStep[],
  incoming: ExecutionStep[]
): ExecutionStep[] => {
  const byId = new Map<string, ExecutionStep>();
  for (const step of previous) byId.set(step.id, step);
  for (const step of incoming) byId.set(step.id, step);
  return Array.from(byId.values()).sort((left, right) => left.position - right.position);
};

const mergeExecutionChatMessages = (
  previous: ExecutionChatMessageRow[],
  incoming: ExecutionChatMessageRow[]
): ExecutionChatMessageRow[] => {
  const byId = new Map<string, ExecutionChatMessageRow>();
  for (const message of previous) byId.set(message.id, message);
  for (const message of incoming) byId.set(message.id, message);
  return sortExecutionChatMessages([...byId.values()]);
};

const extractSnapshotDominos = (flowSnapshot: unknown): SnapshotDomino[] => {
  if (!isRecord(flowSnapshot) || !Array.isArray(flowSnapshot.dominos)) return [];

  return flowSnapshot.dominos
    .map((domino): SnapshotDomino | null => {
      if (!isRecord(domino)) return null;
      return {
        ...(typeof domino.position === 'number' ? { position: domino.position } : {}),
        ...(typeof domino.action_type === 'string'
          ? { action_type: domino.action_type }
          : {}),
        ...(typeof domino.label === 'string' ? { label: domino.label } : {}),
      };
    })
    .filter((domino): domino is SnapshotDomino => domino !== null);
};

export const getExecutionTraceMessageText = (
  message: ExecutionChatMessageRow
): string | null => {
  switch (message.kind) {
    case 'assistant_message': {
      const payload = (message as ExecutionChatMessageRow<'assistant_message'>).payload;
      return payload.text.trim() || null;
    }
    case 'action_result': {
      const payload = (message as ExecutionChatMessageRow<'action_result'>).payload;
      return (
        [payload.title, payload.text]
          .filter((value): value is string => Boolean(value?.trim()))
          .join('\n\n') || null
      );
    }
    case 'system_event': {
      const payload = (message as ExecutionChatMessageRow<'system_event'>).payload;
      return payload.text.trim() || null;
    }
    default:
      return null;
  }
};

export const useToolExecutionTrace = (
  executionId: string | null
): ToolExecutionTraceState => {
  const [trace, setTrace] = useState<ToolExecutionTraceState>(
    EMPTY_EXECUTION_TRACE_STATE
  );

  useEffect(() => {
    if (!executionId) {
      setTrace(EMPTY_EXECUTION_TRACE_STATE);
      return;
    }

    let active = true;

    const loadExecutionTrace = async (reason: string) => {
      setTrace((current) => ({
        ...current,
        loading: true,
        error: null,
      }));

      try {
        const [executionRes, stepsRes, messagesRes] = await Promise.all([
          supabase
            .from('flow_executions')
            .select('id, flow_snapshot, input_values')
            .eq('id', executionId)
            .maybeSingle(),
          supabase
            .from('execution_steps')
            .select('*')
            .eq('execution_id', executionId)
            .order('position', { ascending: true }),
          supabase
            .from('execution_chat_messages')
            .select('*')
            .eq('execution_id', executionId)
            .order('created_at', { ascending: true })
            .order('id', { ascending: true })
            .limit(600),
        ]);

        if (!active) return;

        if (executionRes.error && executionRes.error.code !== 'PGRST116') {
          throw executionRes.error;
        }
        if (stepsRes.error) throw stepsRes.error;
        if (messagesRes.error) throw messagesRes.error;

        const execution = executionRes.data as
          | { flow_snapshot?: unknown; input_values?: unknown }
          | null;

        setTrace({
          loading: false,
          error: null,
          steps: ((stepsRes.data ?? []) as ExecutionStep[]).sort(
            (left, right) => left.position - right.position
          ),
          messages: sortExecutionChatMessages(
            (messagesRes.data ?? []) as ExecutionChatMessageRow[]
          ),
          snapshotDominos: extractSnapshotDominos(execution?.flow_snapshot),
          launchInputs: extractLaunchInputs(execution?.input_values, execution?.flow_snapshot),
        });
      } catch (loadError) {
        if (!active) return;
        console.warn('Failed to load tool execution trace:', reason, loadError);
        setTrace((current) => ({
          ...current,
          loading: false,
          error:
            loadError instanceof Error
              ? loadError.message
              : 'Impossible de charger la trace d’exécution.',
        }));
      }
    };

    void loadExecutionTrace('initial');

    const channel = supabase
      .channel(`tool-execution-trace-${executionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'execution_steps',
          filter: `execution_id=eq.${executionId}`,
        },
        (payload) => {
          if (!active || payload.eventType === 'DELETE') return;
          const updated = payload.new as ExecutionStep | null;
          if (!updated || typeof updated.id !== 'string') return;
          setTrace((current) => ({
            ...current,
            steps: mergeExecutionSteps(current.steps, [updated]),
          }));
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'execution_chat_messages',
          filter: `execution_id=eq.${executionId}`,
        },
        (payload) => {
          if (!active) return;
          if (payload.eventType === 'DELETE') {
            const deleted = payload.old as { id?: unknown } | null;
            if (typeof deleted?.id !== 'string') return;
            setTrace((current) => ({
              ...current,
              messages: current.messages.filter((message) => message.id !== deleted.id),
            }));
            return;
          }

          const message = payload.new as ExecutionChatMessageRow | null;
          if (!message || typeof message.id !== 'string') return;
          setTrace((current) => ({
            ...current,
            messages: mergeExecutionChatMessages(current.messages, [message]),
          }));
        }
      )
      .subscribe((status) => {
        if (!active) return;
        if (status === 'SUBSCRIBED') {
          void loadExecutionTrace('realtime_subscribed');
        }
      });

    return () => {
      active = false;
      void supabase.removeChannel(channel);
    };
  }, [executionId]);

  return trace;
};
